import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Sparkles, ShieldCheck, Zap, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { appEventName, siteConfig, storageKey } from "../lib/siteConfig";
import { applyAppIdentity, isAdminAppPath } from "../lib/appIdentity";
import { isNativeAdmin } from "../lib/nativeAdmin";

const DISMISS_KEY = storageKey("install_dismissed_at");
const SHOWN_KEY = storageKey("install_shown_in_session");
const OPEN_INSTALL_EVENT = appEventName("open-install");
const DISMISS_DAYS = 3;

function dismissedRecently(key = DISMISS_KEY) {
  const ts = Number(localStorage.getItem(key) || 0);
  return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function detectBrowser() {
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Edg\//.test(ua)) return "edge";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

let savedDeferredPrompt = null;
let savedPromptIsAdmin = null;

/**
 * Module-level helper so the navbar button can open the dialog manually.
 */
export function openInstallPrompt() {
  if (isNativeAdmin()) return;
  if (isAdminAppPath(window.location.pathname)) {
    window.location.assign("/admin/app");
    return;
  }
  // remove the dismissal so the dialog re-shows
  localStorage.removeItem(DISMISS_KEY);
  localStorage.removeItem(`${DISMISS_KEY}:admin`);
  window.dispatchEvent(new CustomEvent(OPEN_INSTALL_EVENT));
}

export default function InstallPrompt() {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [installing, setInstalling] = useState(false);
  const browser = detectBrowser();
  const isAuthed = user && user !== false;
  const adminApp = isAdminAppPath(location.pathname);
  const appName = adminApp ? "Sophia Admin" : siteConfig.name;
  const dismissKey = adminApp ? `${DISMISS_KEY}:admin` : DISMISS_KEY;
  const shownKey = adminApp ? `${SHOWN_KEY}:admin` : SHOWN_KEY;

  useEffect(() => {
    applyAppIdentity(location.pathname);
    setOpen(false);
    if (savedPromptIsAdmin !== adminApp) savedDeferredPrompt = null;
  }, [adminApp, location.pathname]);

  const tryOpen = useCallback((force = false) => {
    if (isNativeAdmin() || adminApp) return;
    if (isStandalone()) return;
    if (!force && dismissedRecently(dismissKey)) return;
    if (!force && adminApp && !isAuthed) return;
    if (!force && !isAuthed && location.pathname === "/") return;
    setOpen(true);
  }, [isAuthed, location.pathname, adminApp, dismissKey]);

  useEffect(() => {
    // Capture the beforeinstallprompt event whenever Chrome / Edge / Android fires it.
    const onBefore = (e) => {
      e.preventDefault();
      savedDeferredPrompt = e;
      savedPromptIsAdmin = adminApp;
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // Auto-show once per session after a short delay.
    if (!sessionStorage.getItem(shownKey)) {
      const t = setTimeout(() => {
        tryOpen();
        sessionStorage.setItem(shownKey, "1");
      }, 1600);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, [tryOpen, shownKey, adminApp]);

  useEffect(() => {
    // Manual trigger from navbar
    const onForce = () => tryOpen(true);
    window.addEventListener(OPEN_INSTALL_EVENT, onForce);
    return () => window.removeEventListener(OPEN_INSTALL_EVENT, onForce);
  }, [tryOpen]);

  const install = async () => {
    if (!savedDeferredPrompt) {
      // No native prompt available — instructions already visible in body
      return;
    }
    setInstalling(true);
    try {
      await savedDeferredPrompt.prompt();
      const choice = await savedDeferredPrompt.userChoice;
      if (choice?.outcome !== "accepted") {
        localStorage.setItem(dismissKey, String(Date.now()));
      }
      setOpen(false);
      savedDeferredPrompt = null;
    } catch {
      // ignore
    } finally {
      setInstalling(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(dismissKey, String(Date.now()));
    setOpen(false);
  };

  if (!open) return null;

  const hasNativePrompt = !!savedDeferredPrompt && savedPromptIsAdmin === adminApp;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-end md:place-items-center bg-black/70 backdrop-blur-sm p-4 md:p-6"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="install-prompt"
        className="w-full max-w-md rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0B2115] via-[#0A130E] to-[#030705] p-6 md:p-7 shadow-2xl shadow-black/60 fade-up"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37] overflow-hidden shadow-lg shadow-[#D4AF37]/30">
              <img src={adminApp ? "/admin/icon-192.png" : siteConfig.logoUrl} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold leading-tight">Install {appName}</h3>
              <p className="text-xs text-white/50">Get the full-screen app experience</p>
            </div>
          </div>
          <button onClick={dismiss} data-testid="install-dismiss" className="p-1 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <ul className="space-y-2 mb-5 text-sm text-white/70">
          <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#D4AF37] shrink-0" /> Standalone window — no browser bar</li>
          <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#D4AF37] shrink-0" /> One-tap launch from home / dock</li>
          <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37] shrink-0" /> {adminApp ? "Your admin tools in one place" : "Quick access to your account"}</li>
        </ul>

        {/* Browser-specific install instructions when native prompt isn't available */}
        {!hasNativePrompt && (
          <div className="rounded-xl border border-[#D4AF37]/20 bg-black/40 p-4 mb-5 text-sm text-white/80">
            {browser === "ios" && (
              <>
                <p className="font-semibold mb-2 text-white">Install on iPhone / iPad</p>
                <ol className="list-decimal pl-5 space-y-1 text-white/70">
                  <li>Tap the <span className="font-mono text-[#D4AF37]">Share</span> button in Safari</li>
                  <li>Choose <span className="font-mono text-[#D4AF37]">Add to Home Screen</span></li>
                  <li>Tap <span className="font-mono text-[#D4AF37]">Add</span></li>
                </ol>
              </>
            )}
            {browser === "android" && (
              <>
                <p className="font-semibold mb-2 text-white">Install on Android</p>
                <ol className="list-decimal pl-5 space-y-1 text-white/70">
                  <li>Tap the <span className="font-mono text-[#D4AF37]">⋮ menu</span> top right</li>
                  <li>Choose <span className="font-mono text-[#D4AF37]">Install app</span> or <span className="font-mono text-[#D4AF37]">Add to Home screen</span></li>
                </ol>
              </>
            )}
            {(browser === "chrome" || browser === "edge") && (
              <>
                <p className="font-semibold mb-2 text-white">Install on Desktop ({browser === "edge" ? "Edge" : "Chrome"})</p>
                <ol className="list-decimal pl-5 space-y-1 text-white/70">
                  <li>Look for the <span className="font-mono text-[#D4AF37]">install icon ⊕</span> in the address bar</li>
                  <li>Or open the <span className="font-mono text-[#D4AF37]">menu → Install {appName}</span></li>
                </ol>
              </>
            )}
            {(browser === "safari" || browser === "firefox" || browser === "other") && (
              <>
                <p className="font-semibold mb-2 text-white">Install {appName}</p>
                <p className="text-white/70">Use your browser's <span className="font-mono text-[#D4AF37]">"Install app"</span> or <span className="font-mono text-[#D4AF37]">"Add to Home Screen"</span> menu option.</p>
                <p className="text-xs text-white/50 mt-2">For the smoothest install, use Chrome on desktop / Android, or Safari on iOS.</p>
              </>
            )}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={dismiss}
            data-testid="install-later"
            className="px-5 py-3 rounded-full border border-white/10 hover:bg-white/5 text-sm text-white/70"
          >
            Not now
          </button>
          <button
            onClick={hasNativePrompt ? install : dismiss}
            disabled={installing}
            data-testid="install-app"
            className="flex min-h-[54px] flex-1 items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] px-6 py-4 text-base font-extrabold text-black shadow-lg shadow-[#D4AF37]/20 transition-all hover:-translate-y-0.5 hover:bg-[#F1C40F] disabled:opacity-50"
          >
            <Download className="h-5 w-5" />
            {installing ? "Installing..." : hasNativePrompt ? "Install app" : "Got it"}
          </button>
        </div>

        <p className="mt-4 text-[10px] text-white/30 text-center">
          {adminApp ? "Admin sign-in required · Internet connection required" : "Free · Uninstall anytime"}
        </p>
      </div>
    </div>
  );
}
